import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import PushPinIcon from '@mui/icons-material/PushPin';
import { Stack } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import { Theme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { DateTime } from 'luxon';
import { useModal } from 'mui-modal-provider';
import * as React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { makeStyles } from 'tss-react/mui';
import { useDeleteNote, useEditNote } from '../../../sic-lib/api/hooks/useNotes';
import { ContactNoteStatus } from '../../../sic-lib/enums/ContactNoteStatus';
import { Contact } from '../../../sic-lib/types/Contact';
import { ContactNote } from '../../../sic-lib/types/ContactNote';
import DialogConfirmAction from '../../Shared/DialogConfirmAction/DialogConfirmAction';

interface Props {
    note: ContactNote;
    contact: Contact;
    onToggleHandler: () => void;
}

export default function ContactNoteDisplay({ note, contact, onToggleHandler }: Props) {
    const { showModal } = useModal();
    const { classes, cx } = useStyles();
    const [isCopied, setIsCopied] = React.useState<boolean>(false);
    const { mutate: deleteNote } = useDeleteNote(contact.contactId);
    const { mutate: editNote } = useEditNote(contact.contactId);
    const formattedDate = React.useMemo(
        () => DateTime.fromISO(note.date).toLocaleString(DateTime.DATE_MED),
        [note.date],
    );

    const handleDelete = () => {
        const modal = showModal(DialogConfirmAction, {
            title: 'Delete Note',
            description: 'This note will be permanently deleted',
            onConfirm: async () => deleteNote(note.noteId),
            onCancel: () => {
                modal.hide();
            },
        });
    };

    const handlePin = async () => {
        editNote({
            ...note,
            ...{
                noteStatus:
                    note.noteStatus === ContactNoteStatus.Active
                        ? ContactNoteStatus.Pinned
                        : ContactNoteStatus.Active,
            },
        });
    };

    const handleCopyKey = () => {
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 3000);
    };

    return (
        <Paper
            className={cx(
                classes.paper,
                note.noteStatus === ContactNoteStatus.Pinned && classes.pinned,
            )}
        >
            <Typography variant="body1" mb={1} className={classes.whitespace}>
                {note.text}
            </Typography>
            <Typography variant="caption" noWrap className={classes.footer}>
                {formattedDate} {'\u00B7'} {note.username}
            </Typography>
            <div
                className={cx(
                    classes.buttons,
                    'note-buttons',
                    note.noteStatus === ContactNoteStatus.Pinned && classes.pinned,
                )}
            >
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title={note.noteStatus === ContactNoteStatus.Pinned ? 'UnpinPin' : ''}>
                        <IconButton size="small" onClick={handlePin}>
                            <PushPinIcon />
                        </IconButton>
                    </Tooltip>
                    <CopyToClipboard text={note.text} onCopy={handleCopyKey}>
                        <Tooltip title={isCopied ? 'Copied' : 'Copy Note'}>
                            <IconButton size="small">
                                <FileCopyIcon />
                            </IconButton>
                        </Tooltip>
                    </CopyToClipboard>
                    <Tooltip title="Delete">
                        <IconButton size="small" onClick={handleDelete}>
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <IconButton size="small" onClick={onToggleHandler}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                </Stack>
            </div>
        </Paper>
    );
}

const useStyles = makeStyles()((theme: Theme, param, classes) => ({
    paper: {
        padding: theme.spacing(2),
        backgroundColor: theme.palette.noteStatus.main,
        position: 'relative',
        //opacity animaiton
        '&:hover .note-buttons': {
            opacity: 1,
        },
    },
    pinned: {
        backgroundColor: theme.palette.noteStatus.light,
    },
    whitespace: {
        whiteSpace: 'pre-wrap',
    },
    footer: {
        display: 'block',
        color: 'rgba(0, 0, 0, 0.6)',
    },
    buttons: {
        position: 'absolute',
        right: theme.spacing(1),
        bottom: theme.spacing(1),
        zIndex: 5,
        backgroundColor: theme.palette.noteStatus.main,
        transition: 'opacity 0.3s ease-in-out',
        opacity: 0,
    },
}));
